import { z } from 'zod';
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js/max';

export const DEFAULT_PHONE_COUNTRY = 'TR';

export const INVALID_PHONE_MESSAGE = 'form.validation.invalidPhone';

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isValidE164Phone(value) {
    if (value == null || value === '') return false;
    if (typeof value !== 'string') return false;
    return isValidPhoneNumber(value);
}

/**
 * Normalize free-text / national / international input to E.164 when possible.
 * @param {unknown} raw
 * @param {string} [defaultCountry]
 * @returns {string|undefined}
 */
export function normalizeToE164(raw, defaultCountry = DEFAULT_PHONE_COUNTRY) {
    if (raw == null) return undefined;
    const trimmed = String(raw).trim();
    if (!trimmed) return undefined;
    try {
        const parsed = parsePhoneNumberFromString(trimmed, defaultCountry);
        if (parsed?.isValid()) return parsed.number;
    } catch {
        // fall through
    }
    return undefined;
}

/**
 * Empty string / null / undefined → undefined; non-empty must be a valid phone.
 */
export const optionalE164PhoneSchema = z
    .union([z.string(), z.null(), z.undefined()])
    .transform((v) => {
        if (v == null) return undefined;
        const trimmed = String(v).trim();
        return trimmed === '' ? undefined : trimmed;
    })
    .refine((v) => v === undefined || isValidE164Phone(v), {
        message: INVALID_PHONE_MESSAGE,
    });

/**
 * Required non-empty E.164 phone.
 */
export const requiredE164PhoneSchema = z
    .string({ required_error: 'form.validation.phoneRequired' })
    .trim()
    .min(1, 'form.validation.phoneRequired')
    .refine((v) => isValidE164Phone(v), {
        message: INVALID_PHONE_MESSAGE,
    });
